import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { History, useHistory } from "@insite/mobius/utilities/HistoryContext";
import React from "react";
import { css } from "styled-components";

const enum fields {
    leftImageUrl = "leftImageUrl",
    leftAltText = "leftAltText",
    overlayImageUrl = "overlayImageUrl",
    overlayAltText = "overlayAltText",
    flipLayout = "flipLayout",
    buttonText = "buttonText",
    buttonPosition = "buttonPosition",
    leftImageSize = "leftImageSize",
    rightImageSize = "rightImageSize",
    buttonLink = "buttonLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.leftImageUrl]: string;
        [fields.leftAltText]: string;
        [fields.overlayImageUrl]: string;
        [fields.overlayAltText]: string;
        [fields.flipLayout]: boolean;
        [fields.buttonText]: string;
        [fields.buttonPosition]: string;
        [fields.leftImageSize]: string;
        [fields.rightImageSize]: string;
        [fields.buttonLink]: LinkFieldValue;
    };
}

const onClick = (history: History, link: string | undefined) => {
    if (!link) {
        return;
    }

    if (link.startsWith("http")) {
        window.location.href = link;
    } else {
        history.push(link);
    }
};

type Props = OwnProps;

const CustomBox: React.FC<Props> = ({ fields, id }) => {
    const { leftImageUrl, leftAltText, overlayImageUrl, overlayAltText, flipLayout, buttonText } = fields;
    const history = useHistory();
    const url = fields.buttonLink ? useGetLink(fields.buttonLink) : null;
    const cardStyles = {
        cardWrapper: {
            css: css`
                height: 100%;
                display: flex;
                @media screen and (max-width: 800px) {
                    flex-direction: column;
                }
            `,
        },
        cardLeft: {
            css: css`
                /* flex-basis: 50%; */
                min-width: 50%;
            `,
        },
        cardRight: {
            css: css`
                display: flex;
                flex-direction: column;
                justify-content: space-around;
                position: relative;
                padding: 2%;
                button {
                    font-weight: 700;
                    color: white;
                    border: none;
                    background: #f56702;
                    position: relative;
                    z-index: 10;
                    width: 28rem;
                    transform: ${!fields.flipLayout ? "translate(-5rem);" : "translate(13rem)"};
                    transform: ${fields.buttonPosition && `translate(${fields.buttonPosition})`};
                    @media screen and (max-width: 800px) {
                        transform: unset;
                        width: 100%;
                    }
                }
            `,
        },
        buttonOverlayContainer: {
            css: css`
                position: absolute;
                width: 30%;
                transform: ${!fields.flipLayout ? "translate(15rem, 0rem);" : "translate(-5rem, 0rem)"};
                @media screen and (max-width: 800px) {
                    width: 25%;
                    transform: ${!fields.flipLayout ? "translate(-14rem, -2rem);" : "translate(-2rem, -2rem)"};
                }
            `,
        },
        imgs: {
            css: css`
                width: 100%;
                height: 100%;
            `,
        },
    };

    return !flipLayout ? (
        <StyledWrapper {...cardStyles.cardWrapper}>
            <StyledWrapper {...cardStyles.cardLeft}>
                <LazyImage width="100%" src={leftImageUrl} altText={leftAltText} />
            </StyledWrapper>
            <StyledWrapper {...cardStyles.cardRight}>
                <Zone contentId={id} zoneName="right-side-card" />
                <Button buttonType="solid" shape="rectangle" onClick={() => onClick(history, url?.url)}>
                    {buttonText}
                    {fields.overlayImageUrl && (
                        <StyledWrapper {...cardStyles.buttonOverlayContainer}>
                            <LazyImage {...cardStyles.imgs} src={overlayImageUrl} altText={overlayAltText} />
                        </StyledWrapper>
                    )}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    ) : (
        <StyledWrapper {...cardStyles.cardWrapper}>
            <StyledWrapper {...cardStyles.cardRight}>
                <Zone contentId={id} zoneName="right-side-card" />
                <Button buttonType="solid" shape="rectangle" onClick={() => onClick(history, url?.url)}>
                    {buttonText}
                    {fields.overlayImageUrl && (
                        <StyledWrapper {...cardStyles.buttonOverlayContainer}>
                            <LazyImage {...cardStyles.imgs} src={overlayImageUrl} altText={overlayAltText} />
                        </StyledWrapper>
                    )}
                </Button>
            </StyledWrapper>
            <StyledWrapper {...cardStyles.cardLeft}>
                <LazyImage width="100%" src={leftImageUrl} altText={leftAltText} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CustomBox,
    definition: {
        fieldDefinitions: [
            {
                name: fields.leftImageUrl,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                tooltip: "Image Size has to be 330 x 229",
                defaultValue: "",
            },
            {
                name: fields.leftImageSize,
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.leftAltText,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.overlayImageUrl,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                tooltip: "Image Size has to be 330 x 229",
                defaultValue: "",
            },
            {
                name: fields.rightImageSize,
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                displayName: "Overlay Recommended Image Size",
            },
            {
                name: fields.overlayAltText,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.flipLayout,
                displayName: "Flip card layout?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.buttonText,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "Submit",
            },
            {
                fieldType: "Translatable",
                name: fields.buttonPosition,
                editorTemplate: "TextField",
                displayName: "button position (xAxis, px / rem / %)",
                defaultValue: "",
            },
            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
